import React, { useState, useMemo } from 'react';
import { useOrders } from '@/hooks/useDashboardQueries';
import { PageHeader } from '@/components/common/PageHeader';
import { MetricCard } from '@/components/common/MetricCard';
import { StatusBadge } from '@/components/common/StatusBadge';
import { DataTable, Column } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { TableSkeleton, MetricCardSkeleton } from '@/components/common/LoadingSkeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { formatINR, formatTimestamp } from '@/lib/utils';
import { Order } from '@/types/trading';
import {
  ListOrdered,
  Search,
  ArrowUpRight,
  ArrowDownRight,
  ShieldCheck,
  Calendar,
  AlertTriangle,
  FileCheck,
  Link2,
} from 'lucide-react';

export const OrdersView: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<string>('ALL');

  const {
    data: ordersData,
    isLoading,
    isError,
    error,
    refetch,
  } = useOrders();

  const orders = useMemo(() => ordersData?.orders || [], [ordersData]);

  // Filtered dataset
  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      const term = searchTerm.toLowerCase();
      const signalIdStr = order.signal_id !== undefined && order.signal_id !== null ? String(order.signal_id) : '';
      const orderIdStr = order.order_id || '';
      const symbolStr = order.symbol || '';
      const reasonStr = order.rejection_reason || order.reason || '';

      const matchesSearch =
        !term ||
        symbolStr.toLowerCase().includes(term) ||
        orderIdStr.toLowerCase().includes(term) ||
        reasonStr.toLowerCase().includes(term) ||
        signalIdStr.toLowerCase().includes(term) ||
        `sig-${signalIdStr}`.toLowerCase().includes(term);

      const matchesStatus =
        selectedStatusFilter === 'ALL' || order.status.toUpperCase() === selectedStatusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [orders, searchTerm, selectedStatusFilter]);

  // Summary Metrics
  const filledOrders = useMemo(() => orders.filter((o) => o.status === 'FILLED' || o.status === 'EXECUTED').length, [orders]);
  const rejectedOrders = useMemo(() => orders.filter((o) => o.status === 'REJECTED').length, [orders]);
  const pendingOrders = useMemo(() => orders.filter((o) => o.status === 'PENDING').length, [orders]);

  // Table Columns Definition
  const columns: Column<Order>[] = [
    {
      key: 'order_id',
      header: 'Order ID',
      sortable: true,
      render: (ord) => (
        <span className="font-mono text-cyan-300 font-bold text-xs">
          {ord.order_id}
        </span>
      ),
    },
    {
      key: 'signal_id',
      header: 'Signal Correlation',
      sortable: true,
      render: (ord) => {
        if (ord.signal_id === undefined || ord.signal_id === null) {
          return <span className="text-slate-500 font-mono text-xs">—</span>;
        }
        return (
          <div className="flex items-center gap-1 font-mono text-xs text-cyan-400">
            <Link2 className="w-3 h-3 text-cyan-500" />
            <span className="font-bold">#SIG-{ord.signal_id}</span>
          </div>
        );
      },
    },
    {
      key: 'timestamp',
      header: 'Timestamp',
      sortable: true,
      render: (ord) => (
        <span className="font-mono text-slate-400 text-xs flex items-center gap-1.5">
          <Calendar className="w-3 h-3 text-slate-500" />
          {formatTimestamp(ord.timestamp, 'compact')}
        </span>
      ),
    },
    {
      key: 'symbol',
      header: 'Symbol',
      sortable: true,
      render: (ord) => (
        <div className="flex items-center gap-1.5">
          <span className="font-bold text-slate-100 font-mono text-sm">{ord.symbol}</span>
          <span className="px-1.5 py-0.2 rounded bg-slate-800 text-[10px] font-mono text-slate-400">
            NSE
          </span>
        </div>
      ),
    },
    {
      key: 'side',
      header: 'Side',
      sortable: true,
      align: 'center',
      render: (ord) => (
        <span
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono font-bold uppercase border ${
            ord.side === 'BUY'
              ? 'bg-emerald-950/70 text-emerald-400 border-emerald-800/60'
              : 'bg-rose-950/70 text-rose-400 border-rose-800/60'
          }`}
        >
          {ord.side === 'BUY' ? (
            <ArrowUpRight className="w-3 h-3 text-emerald-400" />
          ) : (
            <ArrowDownRight className="w-3 h-3 text-rose-400" />
          )}
          {ord.side}
        </span>
      ),
    },
    {
      key: 'quantity',
      header: 'Quantity',
      sortable: true,
      align: 'right',
      render: (ord) => (
        <span className="font-mono text-slate-200 tabular-nums">{ord.quantity}</span>
      ),
    },
    {
      key: 'price',
      header: 'Order Price',
      sortable: true,
      align: 'right',
      render: (ord) => (
        <span className="font-mono text-slate-200 tabular-nums">{formatINR(ord.price)}</span>
      ),
    },
    {
      key: 'order_type',
      header: 'Type',
      align: 'center',
      render: (ord) => (
        <span className="text-[10px] font-mono text-slate-400 bg-slate-900 border border-slate-800 px-1.5 py-0.5 rounded">
          {ord.order_type}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Audit Status',
      align: 'center',
      render: (ord) => (
        <StatusBadge status={ord.status} size="sm" showDot={false} />
      ),
    },
    {
      key: 'rejection_reason',
      header: 'Notes / Diagnostics',
      render: (ord) => {
        const note = ord.rejection_reason || ord.reason || (ord.status === 'FILLED' ? 'Executed (Paper)' : '—');
        return (
          <span className="text-xs text-slate-400 font-mono truncate max-w-xs block" title={note}>
            {note}
          </span>
        );
      },
    },
  ];

  if (isLoading && orders.length === 0) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Order Book Audit"
          icon={ListOrdered}
        />
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <MetricCardSkeleton key={i} />
          ))}
        </div>
        <TableSkeleton rows={5} cols={9} />
      </div>
    );
  }

  if (isError && orders.length === 0) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Order Book Audit"
          icon={ListOrdered}
        />
        <ErrorState
          title="Failed to load orders audit ledger"
          message={error instanceof Error ? error.message : 'API connection error'}
          onRetry={() => refetch()}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="animate-stagger-1">
        <PageHeader
          title="Order Book Audit"
          description="Read-only audit trail of broker order submissions, fills, risk cancellations, and exchange acknowledgments."
          icon={ListOrdered}
          badge={
            <div className="flex items-center gap-2">
              <span className="text-xs font-mono px-2.5 py-0.5 rounded-full bg-slate-900 text-slate-300 border border-slate-800">
                {orders.length} TOTAL SUBMISSIONS
              </span>
              <StatusBadge status="PAPER_MODE" label="PAPER MODE" size="sm" />
            </div>
          }
        />
      </div>

      {/* Summary Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 animate-stagger-2">
        <MetricCard
          title="Filled Orders"
          value={filledOrders}
          subtitle="Successfully allocated & filled"
          variant="emerald"
          icon={FileCheck}
        />

        <MetricCard
          title="Rejected by Risk"
          value={rejectedOrders}
          subtitle="Declined by capacity or risk limits"
          variant={rejectedOrders > 0 ? 'amber' : 'default'}
          icon={AlertTriangle}
        />

        <MetricCard
          title="Pending / Queued"
          value={pendingOrders}
          subtitle="Awaiting broker fill confirmation"
          variant="default"
          icon={ShieldCheck}
        />

        <MetricCard
          title="Broker Mode"
          value="PAPER SIM"
          subtitle="Oracle autonomous VM simulation"
          variant="cyan"
          icon={ListOrdered}
        />
      </div>

      {/* Search & Filter Bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 rounded-lg border border-slate-800 bg-slate-900/40 animate-stagger-3">
        <div className="flex items-center gap-2 w-full sm:w-auto flex-wrap">
          <div className="relative flex-1 sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search symbol, ID, signal ID, notes..."
              className="w-full pl-9 pr-3 py-1.5 text-xs font-mono bg-slate-950/80 border border-slate-800 rounded-md text-slate-200 placeholder-slate-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all duration-150"
            />
          </div>

          <div className="flex items-center gap-1 text-xs font-mono">
            {['ALL', 'FILLED', 'REJECTED', 'PENDING'].map((st) => (
              <button
                key={st}
                onClick={() => setSelectedStatusFilter(st)}
                className={`px-2.5 py-1 rounded text-[11px] font-mono transition-all duration-150 active:scale-95 ${
                  selectedStatusFilter === st
                    ? 'bg-cyan-950/80 text-cyan-300 border border-cyan-800/70 font-semibold'
                    : 'bg-slate-900 text-slate-400 border border-slate-800 hover:text-slate-200'
                }`}
              >
                {st}
              </button>
            ))}
          </div>
        </div>

        <div className="text-xs font-mono text-slate-400">
          Showing {filteredOrders.length} of {orders.length} orders
        </div>
      </div>

      {/* Desktop Dense Table */}
      <div className="hidden md:block animate-stagger-4">
        <DataTable
          data={filteredOrders}
          columns={columns}
          keyExtractor={(ord) => ord.id}
          pageSize={10}
          emptyTitle="No orders found matching filter"
          emptyDescription="There are currently no raw broker orders meeting your criteria."
        />
      </div>

      {/* Mobile / Tablet Responsive Cards */}
      <div className="md:hidden space-y-3 animate-stagger-4">
        {filteredOrders.length === 0 ? (
          <EmptyState
            title="No orders"
            description="No broker orders recorded in the current session."
          />
        ) : (
          filteredOrders.map((ord) => {
            const sideClass =
              ord.side === 'BUY'
                ? 'card-trade-buy'
                : ord.side === 'SELL'
                ? 'card-trade-sell'
                : 'border-slate-800 bg-slate-900/60';

            return (
              <div
                key={ord.id}
                className={`p-4 rounded-lg border space-y-3 interactive-card active:scale-[0.99] transition-all duration-150 ${sideClass}`}
              >
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-slate-100 font-mono text-sm">{ord.symbol}</span>
                    <span className="text-[10px] font-mono text-cyan-400">{ord.order_id}</span>
                  </div>
                  <span className="text-[10px] text-slate-500 font-mono block mt-0.5">
                    {formatTimestamp(ord.timestamp, 'full')}
                  </span>
                </div>
                <StatusBadge status={ord.status} size="sm" showDot={false} />
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs font-mono pt-2 border-t border-slate-800/60">
                <div>
                  <span className="text-[10px] text-slate-500 block">SIDE & TYPE</span>
                  <span className="text-slate-200 font-semibold">{ord.side} ({ord.order_type})</span>
                </div>
                <div className="text-right">
                  <span className="text-[10px] text-slate-500 block">QTY & PRICE</span>
                  <span className="text-slate-200 tabular-nums">{ord.quantity} @ {formatINR(ord.price)}</span>
                </div>
              </div>

              {ord.signal_id !== undefined && ord.signal_id !== null && (
                <div className="pt-2 border-t border-slate-800/60 text-xs font-mono flex items-center justify-between">
                  <span className="text-[10px] text-slate-500">SIGNAL CORRELATION</span>
                  <span className="text-cyan-400 font-bold">#SIG-{ord.signal_id}</span>
                </div>
              )}

              {ord.rejection_reason && (
                <div className="pt-2 border-t border-slate-800/60 text-[11px] font-mono text-amber-400/90 bg-amber-950/30 p-2 rounded border border-amber-900/40">
                  <span className="text-[10px] text-amber-500/80 block uppercase">Rejection Reason</span>
                  <span>{ord.rejection_reason}</span>
                </div>
              )}
            </div>
          );
        })
      )}
      </div>
    </div>
  );
};
